import queryString from 'query-string';

import {ProjectMetadata as ProjectApiResponse} from 'app_api/projects/types';
import {entityData} from 'common/report/util/search/fixtures';
import {getOrderedSearchContextDisplayOptions} from 'common/report/util/search/index';
import {objToQueryString} from 'common/util';
import {SuccessResponse} from 'common/util/shared-client';
import {BoardMoveSetting} from 'common/report/queries/app-api/project-metadata-client';

const PEOPLE_PROPERTY_NAMES = {
  EMAIL: `$email`,
  INDUSTRY: `Industry`,
  IS_LEAD: `Is lead`,
};

export const EVENT_NAMES = {
  VIEWED_REPORT: `Viewed report`,
  REPORT_LOADED: `Report loaded`,
  FUNNEL_QUERY: `Funnel query`,
  BOOKMARK_LOADED: `Bookmark loaded`,
  RETENTION_QUERY: `Retention query`,
};

const EVENT_PROPERTY_NAMES = {
  ACCOUNT_VALUE_ARR: `Account Value (ARR)`,
  CITY: `$city`,
  OS: `$os`,
  TAB: `tab`,
};

interface EnguageSuccessResponse<T> extends SuccessResponse<T> {
  computed_at: string;
  session_id: string;
}

const queryParamsAllReports = {
  query_filters: JSON.stringify({
    email: [],
    type: getOrderedSearchContextDisplayOptions().map((value) => ({value, negated: false})),
  }),
  query_string: ``,
};

const PROJECT_3_METADATA: ProjectApiResponse = {
  name: `Mixpanel (project 3)`,
  id: 3,

  blocking: {
    is_blocked_events: false,
    is_blocked_people: false,
    label: ``,
  },
  cross_project_board_move_setting: BoardMoveSetting.ALLOWED_WITHIN_ORG,
  domain: `mixpanel.com`,
  events_plan_id: 22204,
  events_plan_name: `foobar`,
  external_help_link: [
    {
      link_name: `#mixpanel-data`,
      link_url: `https://mixpanel.slack.com/archives/CM4CHBLLS`,
    },
  ],
  feature_flags: [],
  feature_gate_name: `mtu_enterprise`,
  feature_gates: {},
  hasSensitiveAccess: true,
  has_integrated_arb: true,
  has_integrated_engage: true,
  has_public_dashboards_enabled: false,
  has_workspaces: true,
  is_demo: false,
  onboarding_dashboard_id: null,
  organization_id: 1,
  owner_id: 1,
  people_plan_id: 13928,
  permissions: [],
  project_as_of_date: null,
  role: {},
  timezone: `US/Pacific`,
  type: 1,
  user_count: 20,
  utc_offset: -420,
  workspaces: {},
};

export const API_HANDLERS = {
  // /api/app/py-migration/
  'api/app/py-migration/': {
    status: `ok`,
    results: {
      python3_endpoints: {},
    },
  },
  '/api/app/projects/3/metadata': {
    status: `ok`,
    results: PROJECT_3_METADATA,
  },
  '/api/app/projects/metadata/index': {
    status: `ok`,
    results: {
      3: {
        name: `Mixpanel (project 3)`,
        id: 3,
        owner_id: 1,
        organization_id: 1,
        domain: `mixpanel.com`,
        timezone: `US/Pacific`,
        utc_offset: -420,
        onboarding_dashboard_id: null,
        external_help_link: [
          {
            link_name: `#mixpanel-data`,
            link_url: `https://mixpanel.slack.com/archives/CM4CHBLLS`,
          },
        ],
        feature_flags: [],
        feature_gates: {},
        feature_gate_name: `mtu_enterprise`,
        people_plan_id: 13928,
        events_plan_id: 22204,
      },
    },
  },
  '/api/query/events/names': (): string[] => {
    return [
      EVENT_NAMES.VIEWED_REPORT,
      EVENT_NAMES.REPORT_LOADED,
      EVENT_NAMES.FUNNEL_QUERY,
      EVENT_NAMES.BOOKMARK_LOADED,
      EVENT_NAMES.RETENTION_QUERY,
    ];
  },
  '/api/query/events/properties/toptypes': (url): Record<string, any> => {
    const COMMON_PROPS = {
      [EVENT_PROPERTY_NAMES.CITY]: {count: 91776, type: `string`},
      [EVENT_PROPERTY_NAMES.OS]: {count: 230935, type: `string`},
      [EVENT_PROPERTY_NAMES.ACCOUNT_VALUE_ARR]: {count: 37171465, type: `number`},
    };

    const EVENT_SPECIFIC_PROPS = {
      [EVENT_NAMES.VIEWED_REPORT]: {
        [EVENT_PROPERTY_NAMES.TAB]: {count: 16511673, type: `string`},
      },
    };

    const {query} = queryString.parseUrl(url);

    return {
      ...COMMON_PROPS,
      ...(query.event ? EVENT_SPECIFIC_PROPS[query.event] || {} : EVENT_SPECIFIC_PROPS),
    };
  },
  '/api/query/events/properties/values': (url): string[] => {
    const {query} = queryString.parseUrl(url);
    switch (query.name) {
      case EVENT_PROPERTY_NAMES.ACCOUNT_VALUE_ARR:
        return [`100`, `200`, `300`, `500`];
      case EVENT_PROPERTY_NAMES.CITY:
        return [`San Francisco`, `Seattle`, `New York`];
      case EVENT_PROPERTY_NAMES.OS:
        return [`Mac OS X`, `Android`, `Windows`];
      case EVENT_PROPERTY_NAMES.TAB:
        return [`retention`, `funnels`, `insights`];
      default:
        return [];
    }
  },
  '/api/query/engage/properties': (): EnguageSuccessResponse<any> => {
    return {
      computed_at: `2019-04-22T02:44:17.597657+00:00`,
      results: {
        [PEOPLE_PROPERTY_NAMES.EMAIL]: {count: 28205, type: `string`},
        [PEOPLE_PROPERTY_NAMES.INDUSTRY]: {count: 499313, type: `string`},
        [PEOPLE_PROPERTY_NAMES.IS_LEAD]: {count: 1090209, type: `boolean`},
      },
      session_id: `session_id`,
      status: `ok`,
    };
  },
  '/api/query/engage/values': (url): EnguageSuccessResponse<any> => {
    const {query} = queryString.parseUrl(url);
    let results = [];
    switch (query.property) {
      case PEOPLE_PROPERTY_NAMES.INDUSTRY:
        results = [`Electronics`, `Computer Software`, `Marketing and Advertising`];
        break;
      case PEOPLE_PROPERTY_NAMES.EMAIL:
        results = [`dev.test@mixpanel.com`, `mack.duan@mixpanel.com`, `noj.vek@mixpanel.com`];
        break;
      case PEOPLE_PROPERTY_NAMES.IS_LEAD:
        results = [`true`, `false`];
        break;
    }

    return {
      computed_at: `2019-04-22T02:44:17.597657+00:00`,
      results,
      session_id: `session_id`,
      status: `ok`,
    };
  },
  // eslint-disable-next-line @typescript-eslint/naming-convention
  '/api/app/projects/3/custom_events/': (): {custom_events: any[]} => {
    const customEvents = [
      {
        name: `Viewed report from Firefox`,
        deleted: 0,
        alternatives: [],
        project_id: 3,
        id: 1025653,
        is_collect_everything_event: false,
      },
      {
        name: `Funnel or Retention query`,
        deleted: 0,
        alternatives: [],
        project_id: 3,
        id: 1025654,
        is_collect_everything_event: false,
      },
    ];
    return {custom_events: customEvents};
  },
  '/api/app/me': (): SuccessResponse<any> => {
    const results = {
      projects: {
        3: {
          has_integrated: true,
          name: `mixpanel`,
          owner_id: 1,
          permissions: [],
          role: {id: 1, name: `admin`},
        },
      },
    };
    return {status: `ok`, results};
  },
  '/api/app/projects/3/cohorts/': (): SuccessResponse<any> => {
    const results = [
      {
        behaviors: {},
        description: `Churn risk users`,
        referenced_directly_by_campaigns: [],
        selector: {},
        last_edited: `2019-04-22`,
        id: 1,
        count: 256,
        referenced_directly_by: [],
        name: `Churn risk users`,
        created_by: {
          id: 1,
          name: `Mack Duan`,
          email: `mack.duan@mixpanel.com`,
        },
        referenced_by_campaigns: [],
        referenced_by: [],
      },
      {
        behaviors: {},
        description: `Chrome users`,
        referenced_directly_by_campaigns: [],
        selector: {},
        last_edited: `2019-04-22`,
        id: 2,
        count: 2312,
        referenced_directly_by: [],
        name: `Chrome users`,
        can_view: true,
        can_edit: true,
        created_by: {
          id: 2,
          name: `Mack Duan`,
          email: `mack.duan@mixpanel.com`,
        },
        referenced_by_campaigns: [],
        referenced_by: [],
      },
    ];
    return {status: `ok`, results};
  },
  '/api/query/data_definitions/events': (): SuccessResponse<any[]> => {
    const results = [
      {
        lastVerified: `2017-06-12`,
        count: 2278655,
        mergedEventId: null,
        displayName: ``,
        name: `Viewed report`,
        platforms: [`Web`],
        lastModified: `2019-03-26`,
        tags: [`*important*`, `CSM`],
        collectEverythingEventId: null,
        apiQueryCount: 16937,
        lastModifiedBy: {
          name: `Tim Trefren`,
          email: `tim@mixpanel.com`,
        },
        lastVerifiedBy: {
          name: `Andriy Dyadyura`,
          email: `Andriy.Dyadyura@mixpanel.com`,
        },
        uiQueryCount: 12586,
        dropped: false,
        customEventId: null,
        verified: true,
        hidden: false,
        id: 2,
        merged: false,
        description:
          `A user views a report. This is tracked once per page view. The report name can be found under the` +
          ` "Report Name" property (formerly "tab").`,
      },
    ];
    return {status: `ok`, results};
  },
  '/api/app/projects/3/data-definitions/lookup-tables': (): SuccessResponse<any[]> => {
    const results = [
      {
        id: `-881539264457961012`,
        name: `Testing Dimension Table`,
      },
      {
        id: `4599632823728392153`,
        name: `Country`,
      },
    ];
    return {status: `ok`, results};
  },
  '/api/query/data_definitions/properties': (url): SuccessResponse<any[]> => {
    let results = [];
    const {query} = queryString.parseUrl(url);
    if (query.resourceType === `Event`) {
      results = [
        {
          apiQueryCount: 1731,
          description: `Which report was used? Insights, retention, funnels, etc.`,
          uiQueryCount: 3186,
          dropped: false,
          exampleValue: `Retention`,
          id: 3,
          count: 4273007,
          displayName: `Report name`,
          name: `tab`,
          resourceType: `Event`,
          mergedPropertyId: null,
          hidden: false,
          type: `string`,
          merged: false,
        },
      ];
    }
    return {status: `ok`, results};
  },
  '/api/app/projects/3/data-groups/': (): SuccessResponse<any[]> => {
    const results = [
      {
        data_group_id: `-2525195444797586154`,
        display_name: `Project ID`,
        property_name: `project_id`,
        is_ready: true,
      },
      {
        data_group_id: `9292783`,
        display_name: `Billing Account`,
        property_name: `Project owner billing account ID`,
        is_ready: true,
      },
    ];

    return {status: `ok`, results};
  },
  [`api/app/projects/3/search?${objToQueryString(queryParamsAllReports)}`]: {
    status: `ok`,
    results: entityData,
  },
  '/api/app/avatars': {
    status: `ok`,
    results: {
      base: `https://avatars-base-url`,
      avatars: {
        'id-1': `avatar-1`,
        'id-2': `avatar-2`,
      },
    },
  },
  '/api/app/projects/3/custom_properties/': {results: []},
  '/api/app/projects/3/datasets/': {results: []},
};
